// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { makeAutoObservable, runInAction } from "mobx";

import { APIClient, OfflineAPIClient } from "../api";
import {
  RISK_LEVEL_KEYS,
  RISK_LEVELS,
  RiskLevelKey,
} from "../components/OffenderAssessment/constants";
import {
  CreateEmploymentHistoryInput,
  EmploymentHistory,
  UpdateEmploymentHistoryInput,
} from "../components/OffenderAssessment/EmploymentHistory/constants";
import {
  CreateDrugHistoryInput,
  DrugHistory,
  UpdateDrugHistoryInput,
} from "../components/OffenderAssessment/SubstanceUse/constants";
import {
  DomainConfig,
  getDomainsForAssessmentType,
  getScoreWithAgePoint,
  shouldShowOrasContent,
} from "../components/OffenderAssessment/utils";
import { ActiveFeatureVariants } from "../datastores/types";
import {
  calculateAgeAtDate,
  formatAgeAtAssessmentPointNote,
  splitFullName,
} from "../utils/utils";
import { SARDetailsPresenter } from "./SARDetailsPresenter";

/**
 * Presenter for Offender Assessment section (ORAS domain logic).
 *
 * Error handling: These methods intentionally propagate errors to the calling
 * component (e.g., EmploymentHistoryModal). The modal catches errors and displays
 * user-friendly messages. This keeps error UI concerns in the component layer.
 */
export class OffenderAssessmentPresenter {
  constructor(
    private sarDetailsPresenter: SARDetailsPresenter,
    private apiClient: APIClient | OfflineAPIClient,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  // Delegate to parent for SAR data access
  get SARData() {
    return this.sarDetailsPresenter.SARData;
  }

  get domains(): DomainConfig[] {
    return getDomainsForAssessmentType(this.SARData?.assessmentType ?? null);
  }

  /** Domains excluding substanceUse, which is rendered separately as Substance Abuse History. */
  get domainsWithoutSubstanceUse(): DomainConfig[] {
    return this.domains.filter((d) => d.key !== "substanceUse");
  }

  /** True when an ORAS assessment was actually performed (has date + has scored domains). */
  get hasOrasAssessment(): boolean {
    return this.domains.length > 0 && !!this.SARData?.assessmentDate;
  }

  /** Client's age as of the ORAS assessment date, or null if either date is missing. */
  get ageAtAssessment(): number | null {
    const birthDate = this.SARData?.client?.birthDate;
    const assessmentDate = this.SARData?.assessmentDate;
    if (!birthDate || !assessmentDate) return null;
    return calculateAgeAtDate(birthDate, assessmentDate);
  }

  /** True when this assessment type counts the age point toward Criminal History (ORAS-SRT). */
  get criminalHistoryIncludesAgePoint(): boolean {
    return this.domains.some(
      (d) => d.key === "criminalHistory" && d.includesAgePoint,
    );
  }

  /** Criminal History score as shown on its domain card, including the age point when the tool scores one. */
  get criminalHistoryDisplayScore(): number | null {
    return getScoreWithAgePoint(
      { includesAgePoint: this.criminalHistoryIncludesAgePoint },
      this.SARData?.criminalHistoryLevel,
      this.SARData?.ageLevel,
    );
  }

  /** The age point and its subtitle for the Criminal History card, or null when this assessment doesn't score one. */
  get criminalHistoryAgePoint(): { ageLevel: number; note: string } | null {
    const ageLevel = this.SARData?.ageLevel;
    if (!this.criminalHistoryIncludesAgePoint || ageLevel == null) return null;
    return {
      ageLevel,
      note: formatAgeAtAssessmentPointNote(this.ageAtAssessment),
    };
  }

  get groupedByRisk(): Record<RiskLevelKey, string[]> {
    const sarData = this.SARData;
    const grouped: Record<RiskLevelKey, string[]> = {
      HIGH: [],
      MODERATE: [],
      LOW: [],
    };
    if (!sarData) return grouped;
    for (const domain of this.domains) {
      if (!domain.riskLevelField) continue;
      // Dynamic field access is necessary here: domain configs store risk-level
      // field names as strings, so keyof-narrowing is the best available approach.
      const level = sarData[
        domain.riskLevelField as keyof typeof sarData
      ] as RiskLevelKey | null;
      if (level && level in grouped) grouped[level].push(domain.title);
    }
    return grouped;
  }

  get offenderAssessmentDisplay(): string | null {
    const grouped = this.groupedByRisk;
    const parts = RISK_LEVEL_KEYS.filter(
      (level) => grouped[level].length > 0,
    ).map(
      (level) =>
        `${RISK_LEVELS[level].toLowerCase()} in ${grouped[level].join("; ")}`,
    );
    return parts.length > 0 ? `Offender scored ${parts.join(" and ")}.` : null;
  }

  getDomainSummary(domain: DomainConfig): string | null {
    const sarData = this.SARData;
    if (!sarData) return null;
    return sarData[domain.summaryField] ?? null;
  }

  // Employment History - Individual CRUD operations
  // TODO(OBT-29467): remove once import skips manually-updated SARs
  get employmentHistories(): EmploymentHistory[] {
    return this.sarDetailsPresenter.employmentHistories;
  }

  async createEmploymentHistory(
    data: CreateEmploymentHistoryInput,
  ): Promise<void> {
    if (!this.SARData) return;

    const result = await this.apiClient.createEmploymentHistory({
      sarId: this.SARData.id,
      ...data,
    });

    runInAction(() => {
      if (this.SARData) {
        this.SARData.employmentHistories = [
          ...this.SARData.employmentHistories,
          result,
        ];
      }
    });
  }

  async updateEmploymentHistory(
    id: string,
    data: UpdateEmploymentHistoryInput,
  ): Promise<void> {
    if (!this.SARData) return;

    await this.apiClient.updateEmploymentHistory({ id, ...data });

    runInAction(() => {
      if (this.SARData) {
        const index = this.SARData.employmentHistories.findIndex(
          (e) => e.id === id,
        );
        if (index !== -1) {
          this.SARData.employmentHistories[index] = {
            ...this.SARData.employmentHistories[index],
            ...data,
          };
        }
      }
    });
  }

  async deleteEmploymentHistory(id: string): Promise<void> {
    if (!this.SARData) return;

    await this.apiClient.deleteEmploymentHistory({ id });

    runInAction(() => {
      if (this.SARData) {
        this.SARData.employmentHistories =
          this.SARData.employmentHistories.filter((e) => e.id !== id);
      }
    });
  }

  get clientFirstName(): string {
    return splitFullName(this.SARData?.client?.fullName).firstName;
  }

  // Substance Use History - Individual CRUD operations
  get drugHistories(): DrugHistory[] {
    return this.SARData?.drugHistories ?? [];
  }

  get ORASDomainsAvailable(): boolean {
    return this.SARData?.ORASDomainsAvailable ?? true;
  }

  /**
   * Whether ORAS domain scores (risk category summary, domain score cards)
   * should be shown. Behind the SARManualORAS feature variant, staff can mark
   * ORAS domains as unavailable and provide a reason instead of scores — in
   * that case the derived risk-category summary has no data to show.
   *
   * When the feature variant is off, domains are always considered available
   * so existing behavior for tenants without manual ORAS entry is unchanged.
   */
  shouldShowRiskProfileSummary(
    activeFeatureVariants: ActiveFeatureVariants,
  ): boolean {
    if (!this.hasOrasAssessment) return false;
    return shouldShowOrasContent(
      this.ORASDomainsAvailable,
      activeFeatureVariants,
    );
  }

  async createDrugHistory(data: CreateDrugHistoryInput): Promise<void> {
    if (!this.SARData) return;

    const result = await this.apiClient.createDrugHistory({
      sarId: this.SARData.id,
      ...data,
    });

    runInAction(() => {
      if (this.SARData && result) {
        this.SARData.drugHistories = [
          ...this.SARData.drugHistories,
          { ...data, id: result.id },
        ];
      }
    });
  }

  async updateDrugHistory(
    id: string,
    data: UpdateDrugHistoryInput,
  ): Promise<void> {
    if (!this.SARData) return;

    await this.apiClient.updateDrugHistory({ id, ...data });

    runInAction(() => {
      if (this.SARData) {
        const index = this.SARData.drugHistories.findIndex((e) => e.id === id);
        if (index !== -1) {
          this.SARData.drugHistories[index] = {
            ...this.SARData.drugHistories[index],
            ...data,
          };
        }
      }
    });
  }

  async deleteDrugHistory(id: string): Promise<void> {
    if (!this.SARData) return;

    await this.apiClient.deleteDrugHistory({ id });

    runInAction(() => {
      if (this.SARData) {
        this.SARData.drugHistories = this.SARData.drugHistories.filter(
          (e) => e.id !== id,
        );
      }
    });
  }

  async updateDrugHistorySummary(value: string): Promise<void> {
    return this.sarDetailsPresenter.updateStringField(
      "drugHistorySummary",
      value,
    );
  }
}
